import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { Cell, Pie, PieChart } from 'recharts';
import { Legend } from './Legend';

const Container = styled.div`
  justify-content: center;
`;

const DonutContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`;

const StyledCircle = styled.div < { marginLeft: string, onClick: any, isDisabled: boolean }>`
  background-color: white;
  border-radius: 50%;
  box-sizing: content-box;
  color: #315c89;
  display: flex;
  height: 120px;
  justify-content: center;
  margin-top: 45px;
  margin-left: ${props => props.marginLeft || '18px'};
  position: absolute;
  text-align: center;
  text-transform: uppercase;
  width: 120px;
  ${(props: { onClick: any, isDisabled: boolean }) =>
  (props.onClick !== undefined && !props.isDisabled) &&
    css`
      &:hover {
        background-color: #f4f4f4; //fallback for IE
        background-color: rgb(211, 211, 211, 0.25);
        color: #043358;
        cursor: pointer;
      }
    `}
`;

const StyledCount = styled.div<{ paddingTop: string }>`
  font-family: 'Roboto';
  font-size: 45px;
  padding-top: ${props => props.paddingTop || '20px'};
`;

const StyledLabel = styled.div<{ nowrap?: boolean }>`
  font-family: 'Roboto';
  font-size: 14px;
  width: 80px;
  ${props => props.nowrap && 'white-space: nowrap; min-width: 80px;'}
`;

export interface IDonutChartProps {
  colors: string[];
  data: Array<IDataProps>;
  'data-testid'?: string;
  displayValue?: string;
  hasLegend?: boolean;
  isAnimationActive? : boolean;
  isDisabled?: boolean;
  labelTitle: string;
  labelNoWrap?: boolean;
  legendMarginLeft?: string;
  marginLeft?: string;
  noRounding?: boolean;
  onClick?: () => void;
  paddingTop?: string;
  startAngle?: number;
}

export interface IDataProps {
  className?: string;
  isDisabled?: boolean;
  name: string;
  onClick?: () => void;
  value: number;
}

const truncatedRoundedNumber = (count: string) => {
  const countAsInt = parseInt(count);
  if (isNaN(count as any) || countAsInt < 1000) {
    return count;
  }

  //The minus 1 is in here so that 1050 to round down to 1k and not up to 1.1k
  if (countAsInt < 9951) {
    return (parseInt(Math.round((countAsInt - 1) / 100) as any) / 10).toString().concat('k');
  }
  return (parseInt(Math.round((countAsInt - 1) / 1000) as any)).toString().concat('k');
};

export const Donut: FC<IDonutChartProps> = props => {
  let orderTotal = props.displayValue ?? props.data.reduce((count, current) => count + current.value, 0).toString();
  orderTotal = props.noRounding ? orderTotal : truncatedRoundedNumber(orderTotal);
  const styledCountPaddingTopDefault = props.labelTitle == "" ? "35px" : "20px";
  const dataSum = props.data.reduce((count, current) => count + current.value, 0);
  const pieData = dataSum === 0 ? [{ name: 'placeholder', value: 1 }] : props.data;

  return (
    <Container>
      <DonutContainer data-testid={props['data-testid']}>
        <PieChart height={233} width={233}>
          <Pie
            cx={120}
            cy={100}
            data={pieData}
            dataKey="value"
            innerRadius={80}
            isAnimationActive={props.isAnimationActive !== undefined ? props.isAnimationActive : true}
            outerRadius={100}
            startAngle={props.startAngle}>
            {pieData.map((entry, index) => (
              <Cell
                className={entry.className}
                cursor={(entry.onClick && !entry.isDisabled) ? "pointer" : "default"}
                fill={props.colors[index % props.colors.length]}
                key={`cell-${index}`}
                onClick={entry.isDisabled ? undefined : entry.onClick} />
            ))}
          </Pie>
        </PieChart>
        <StyledCircle
          isDisabled={props.isDisabled ? props.isDisabled : false}
          marginLeft={props.marginLeft || '20px'}
          onClick={props.isDisabled ? undefined : props.onClick}
          >
          <StyledCount paddingTop={props.paddingTop ? props.paddingTop : styledCountPaddingTopDefault}>
            {orderTotal} <StyledLabel nowrap={props.labelNoWrap}>{props.labelTitle}</StyledLabel>
          </StyledCount>
        </StyledCircle>
      </DonutContainer>
      {props.hasLegend ? <LegendContainer><Legend colors={props.colors} data={props.data} marginLeft={props.legendMarginLeft} /> </LegendContainer> : null}
    </Container>
  );
};

